/**
 * Pure helper that maps a tenant_integrations row to the SyncStatusCard's
 * displayed status. Lives client-side because the derivation is a small
 * function of three columns; the architectural decision to keep this
 * client-side (instead of an `/sync-status` endpoint) is documented in the
 * Story 1.2 spec under "Architectural decision" with an escalation rule.
 *
 * Story 1.4 (disable/enable) and Story 4.1 (auth_failed handler) write the
 * inputs to this function. Epic 4 may introduce a 'paused' sentinel — keep
 * the helper isolated so adapting to that change is a one-file edit.
 */

import type { SubContractorHubIntegration } from '@/types/integrations/subcontractorhub';

export type IntegrationStatus =
  | 'active'
  | 'disabled'
  | 'auth_failed'
  | 'paused'
  | 'paused_drop_detected'
  | 'paused_failures'
  | 'not_configured';

export function deriveIntegrationStatus(
  integration: SubContractorHubIntegration | null,
): IntegrationStatus {
  if (!integration) return 'not_configured';
  // auth_failed wins over enabled/disabled — the user must recover via
  // re-test connection (Story 1.2 AC4) before any other action makes sense.
  if (integration.lastSyncStatus === 'auth_failed') return 'auth_failed';
  // SCH-5.5: cron-tick detected a >50% project-count drop and paused sync.
  // Distinct from generic 'paused' so the FE can surface the actionable
  // "Resume after drop" UX vs. a regular paused message.
  if (integration.lastSyncStatus === 'paused_drop_detected') return 'paused_drop_detected';
  // SCH-5.7: auto-paused after N consecutive failed scheduled syncs. Distinct
  // from generic 'paused' so the FE can surface the "Resume scheduled sync" CTA
  // + explanatory caption.
  if (integration.lastSyncStatus === 'paused_failures') return 'paused_failures';
  if (integration.lastSyncStatus === 'paused') return 'paused';
  if (!integration.enabled) return 'disabled';
  // SCH-BUG-9a (P2): only null/'active' are healthy. An UNRECOGNIZED non-null
  // status (e.g. a new backend pause state this FE build predates) must NOT
  // fail open to 'active' and mask a problem — surface it as the neutral
  // 'paused' ("see logs") state instead.
  if (integration.lastSyncStatus == null || integration.lastSyncStatus === 'active') {
    return 'active';
  }
  return 'paused';
}
